import React, { createContext, useState, useCallback, ReactNode } from 'react';
import {
  userManagementApi,
  UserDto,
  SiteDetailDto,
  CreateSiteRequestDto,
  CreateUserRequestDto,
  UpdateUserRequestDto,
  CreateUserResponseDto,
} from '../services/userManagementApi';

// ── Context Shape ──────────────────────────────────────────────────────────

interface UserManagementContextType {
  // State
  Users: UserDto[];
  User: UserDto | null;
  sites: SiteDetailDto[];
  loading: boolean;
  error: string | null;

  // Actions
  getAllUsers: () => Promise<UserDto[]>;
  getUserById: (_UserId: number) => Promise<UserDto | null>;
  createUser: (_data: CreateUserRequestDto) => Promise<CreateUserResponseDto | null>;
  updateUser: (_UserId: number, _data: UpdateUserRequestDto) => Promise<boolean>;
  deleteUser: (_UserId: number) => Promise<boolean>;
  getSites: () => Promise<SiteDetailDto[]>;
  createSite: (_data: CreateSiteRequestDto) => Promise<SiteDetailDto | null>;

  // Utility
  clearError: () => void;
}

export const UserManagementContext = createContext<UserManagementContextType | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────────────

interface UserManagementProviderProps {
  children: ReactNode;
}

export const UserManagementProvider: React.FC<UserManagementProviderProps> = ({ children }) => {
  const [Users, setUsers] = useState<UserDto[]>([]);
  const [User, setUser] = useState<UserDto | null>(null);
  const [sites, setSites] = useState<SiteDetailDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const getAllUsers = useCallback(async (): Promise<UserDto[]> => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementApi.getAllUsers();
      setUsers(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch Users';
      setError(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserById = useCallback(async (UserId: number): Promise<UserDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementApi.getUserById(UserId);
      setUser(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch User';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = useCallback(async (data: CreateUserRequestDto): Promise<CreateUserResponseDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const result = await userManagementApi.createUser(data);
      if (result.success && result.user) {
        setUsers(prev => [result.user!, ...prev]);
      }
      return result;
    } catch (err: any) {
      const msg = err.message || 'Failed to create User';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (UserId: number, data: UpdateUserRequestDto): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      const success = await userManagementApi.updateUser(UserId, data);
      if (success) {
        const patch = { ...data, role: data.role as UserDto['role'] };
        setUsers(prev => prev.map(u => (u.userId === UserId ? { ...u, ...patch } : u)));
        if (User?.userId === UserId) setUser(prev => prev ? { ...prev, ...patch } : prev);
      }
      return success;
    } catch (err: any) {
      const msg = err.message || 'Failed to update User';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [User]);

  const createSite = useCallback(async (data: CreateSiteRequestDto): Promise<SiteDetailDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const site = await userManagementApi.createSite(data);
      setSites(prev => [...prev, site]);
      return site;
    } catch (err: any) {
      const msg = err.message || 'Failed to create site';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSites = useCallback(async (): Promise<SiteDetailDto[]> => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementApi.getSites();
      setSites(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch sites';
      setError(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (UserId: number): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await userManagementApi.deleteUser(UserId);
      setUsers(prev => prev.filter(t => t.userId !== UserId));
      if (User?.userId === UserId) setUser(null);
      return true;
    } catch (err: any) {
      const msg = err.message || 'Failed to delete User';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [User]);

  const contextValue: UserManagementContextType = {
    Users,
    User,
    sites,
    loading,
    error,
    getAllUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    getSites,
    createSite,
    clearError,
  };

  return (
    <UserManagementContext.Provider value={contextValue}>
      {children}
    </UserManagementContext.Provider>
  );
};
